import {
  CircularProgressBar,
  GeneratingExtraction,
  LoadingSpinner,
  PDFHighlightPreview,
  ServiceOptions
} from 'app/components';
import React, { useEffect, useState } from 'react';
import styles from './DocumentCompare.module.scss';
import { setCurrentFile, useAppSelector } from 'app/redux';
import { useDispatch } from 'react-redux';
import {
  getService,
  setGeneratingExtraction,
  setRegenerateExtraction
} from 'app/redux/slices/settings-action';
import { handleFileSelection, handleFileValidation } from 'app/utils';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/Attach.svg';
import { ReactComponent as RepeatIcon } from 'app/assets/Icons/Repeat.svg';
import { Link } from '@zurich-data-dsm-icons/action.link';
import classnames from 'classnames';
import FormData from 'form-data';
import { compareDocuments } from 'integration/extraction/api';
import { DiffCompare } from './DiffCompare';
import { emptyPDFBase64 } from './const';
import { notify } from '@zurich-base-ui/components.toast';

interface DiffEntry {
  text: string;
  op: 'add' | 'none' | 'remove';
}

export const DocumentCompare: React.FC = () => {
  const AVG_DURATION_IN_MS = 30000;
  const dispatch = useDispatch();
  const currentService = useAppSelector(getService);

  const [additions, setAdditions] = useState(0);
  const [removals, setRemovals] = useState(0);
  const [sourcePDF, setSourcePDF] = useState<File | undefined>(undefined);
  const [sourceDiff, setSourceDiff] = useState<DiffEntry[]>([]);
  const [emptyPDF, setEmptyPDF] = useState<File | undefined>(undefined);
  const [targetDiff, setTargetDiff] = useState<DiffEntry[]>([]);
  const [targetPDF, setTargetPDF] = useState<File | undefined>(undefined);

  const [similarityScore, setSimilarityScore] = useState(0);
  const [renderDiff, setRenderDiff] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  let pdfField: undefined | string;

  useEffect(() => {
    setEmptyFile();
  }, []);

  useEffect(() => {
    if (emptyPDF) {
      setIsLoading(false);
    }
  }, [emptyPDF]);

  const setEmptyFile = async (): Promise<void> => {
    const res: Response = await fetch(emptyPDFBase64);
    const blob: Blob = await res.blob();
    const file = new File([blob], 'empty.pdf', { type: 'image/png' });
    setEmptyPDF(file);
  };

  const comparePDFs = async (): Promise<void> => {
    const body = new FormData();
    body.append('source', sourcePDF, sourcePDF?.name);
    body.append('target', targetPDF, targetPDF?.name);
    setIsProcessing(true);
    dispatch(setGeneratingExtraction(true));

    await compareDocuments('cross-functional', String(currentService), body)
      .then((response) => {
        const sDiff: DiffEntry[] = [];
        const tDiff: DiffEntry[] = [];
        let add = 0;
        let rem = 0;

        if (response?.diff_text) {
          for (const e of response.diff_text as Array<Array<string | number>>) {
            const [op, text] = e;
            if (op === -1) {
              sDiff.push({ text: '', op: 'add' });
              tDiff.push({ text: text as string, op: 'none' });
              add++;
            } else if (op === 1) {
              sDiff.push({ text: text as string, op: 'none' });
              tDiff.push({ text: '', op: 'remove' });
              rem++;
            } else {
              sDiff.push({ text: text as string, op: 'none' });
              tDiff.push({ text: text as string, op: 'none' });
            }
          }
        }
        setSourceDiff(sDiff);
        setTargetDiff(tDiff);
        setSimilarityScore(response.similarity_score);
        setAdditions(add);
        setRemovals(rem);

        setIsProcessing(false);
        setRenderDiff(true);
      })
      .catch((e) => {
        console.log(e);
        notify({ type: 'error', message: String(e) });
        setIsProcessing(false);
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = e.target;
    const selectedFile: File | undefined = files ? files[0] : undefined;

    if (selectedFile !== undefined) {
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        dispatch(setCurrentFile(undefined));
        dispatch(setCurrentFile(files?.[0]));
        dispatch(setRegenerateExtraction(true));
        if (pdfField === 'source') {
          setSourcePDF(files?.[0]);
        } else {
          setTargetPDF(files?.[0]);
        }
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
      }
    }
  };

  const handleUploadNewFile = (e: any, selectedPDF: string): void => {
    e.stopPropagation();
    pdfField = selectedPDF;
    handleFileSelection(handleFileChange, currentService);
  };

  const handleFileDrop = (
    e: React.DragEvent<HTMLDivElement>,
    selectedPDF: string
  ): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];

    if (file !== undefined) {
      const validation = handleFileValidation(file, currentService);
      if (validation.valid) {
        dispatch(setCurrentFile(file));
        if (selectedPDF === 'source') {
          setSourcePDF(file);
        } else {
          setTargetPDF(file);
        }
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  return (
    <div className={styles['service-container']}>
      <h1>Document Compare</h1>
      <div className={styles['service-options']}>
        <ServiceOptions showUseOurApiVersion={true} />

        {renderDiff && (
          <Button
            aria-label="Regenerate response"
            variant="tertiary"
            size="small"
            style={{ padding: '0.625rem 0' }}
            onClick={() => {
              setRenderDiff(false);
              setSourcePDF(undefined);
              setTargetPDF(undefined);
            }}
          >
            <Button.Icon>
              <RepeatIcon />
            </Button.Icon>
            <Button.Label>Regenerate</Button.Label>
          </Button>
        )}
      </div>

      {!renderDiff && (
        <>
          <div className={styles.wrapper}>
            <div className={styles['file-translator-container']}>
              <PDFHighlightPreview
                highlightCoords={undefined}
                selectedHighlight={undefined}
                showHighlights={false}
                displayFile={sourcePDF || emptyPDF}
              />
              {sourcePDF === undefined && (
                /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
                <div
                  className={classnames(styles['upload-section'], {
                    [styles.dragging]: isDragging
                  })}
                  onDrop={(e) => {
                    handleFileDrop(e, 'source');
                  }}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleUploadNewFile(e, 'source');
                    }
                  }}
                >
                  <p>Drag your file here to compare it, or</p>
                  <Button
                    onClick={(e) => handleUploadNewFile(e, 'source')}
                    size="small"
                  >
                    <Button.Icon>
                      <AttachIcon />
                    </Button.Icon>
                    <Button.Label>Select from your computer</Button.Label>
                  </Button>
                  <p className={styles['supported-files']}>We support .pdf</p>
                </div>
              )}
            </div>
            <div className={styles['file-translator-container']}>
              <PDFHighlightPreview
                highlightCoords={undefined}
                selectedHighlight={undefined}
                showHighlights={false}
                displayFile={targetPDF || emptyPDF}
              />
              {targetPDF === undefined && (
                /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
                <div
                  className={classnames(styles['upload-section'], {
                    [styles.dragging]: isDragging
                  })}
                  onDrop={(e) => {
                    handleFileDrop(e, 'target');
                  }}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleUploadNewFile(e, 'target');
                    }
                  }}
                >
                  <p>Drag your file here to compare it, or</p>
                  <Button
                    onClick={(e) => handleUploadNewFile(e, 'target')}
                    size="small"
                  >
                    <Button.Icon>
                      <AttachIcon />
                    </Button.Icon>
                    <Button.Label>Select from your computer</Button.Label>
                  </Button>
                  <p className={styles['supported-files']}>We support .pdf</p>
                </div>
              )}
            </div>
          </div>
          <div className={styles.settings}>
            <br />
            <Button
              onClick={comparePDFs}
              variant="primary"
              disabled={!(sourcePDF && targetPDF)}
            >
              <Button.Icon>
                <Link />
              </Button.Icon>
              <Button.Label>Compare PDFs</Button.Label>
            </Button>
          </div>
        </>
      )}
      {isProcessing && (
        <GeneratingExtraction processTime={AVG_DURATION_IN_MS} />
      )}
      {renderDiff && (
        <>
          <div className={styles.summary}>
            <div className={styles['summary-child1']}>
              <h3>Similarity score</h3>
              <CircularProgressBar progress={similarityScore * 100} />
            </div>

            <div className={styles['summary-child2']}>
              <h3>Total changes</h3>
              <span>{additions + removals}</span>
            </div>

            <div className={styles['summary-child3']}>
              <h3>Insertions</h3>
              <span>{additions}</span>
            </div>
            <div className={styles['summary-child4']}>
              <h3>Deletion</h3>
              <span>{removals}</span>
            </div>
          </div>
          <div className={styles.wrapper}>
            <DiffCompare
              diffText={sourceDiff}
              originalFile={sourcePDF as File}
            />
            <DiffCompare
              diffText={targetDiff}
              originalFile={targetPDF as File}
            />
          </div>
        </>
      )}
    </div>
  );
};
